import { useEffect, useState } from "react";
import { X, Download, Sparkles } from "lucide-react";

/**
 * PWA install prompt.
 *
 * - Android / desktop Chrome: uses the standard `beforeinstallprompt` event.
 * - iOS Safari: no programmatic install exists, so we show a custom card with
 *   "Tap Share → Add to Home Screen" instructions.
 * - Dismissal is remembered for 7 days via localStorage.
 * - Never shows if the app is already installed (display-mode: standalone).
 */

const DISMISS_KEY = "gg_pwa_prompt_dismissed_at";
const DISMISS_DAYS = 7;

function isStandalone() {
    if (typeof window === "undefined") return false;
    return (
        window.matchMedia?.("(display-mode: standalone)").matches ||
        window.navigator.standalone === true  // iOS
    );
}

function isIOS() {
    if (typeof window === "undefined") return false;
    const ua = window.navigator.userAgent || "";
    return /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
}

function isRecentlyDismissed() {
    try {
        const raw = localStorage.getItem(DISMISS_KEY);
        if (!raw) return false;
        const then = parseInt(raw, 10);
        return Date.now() - then < DISMISS_DAYS * 24 * 60 * 60 * 1000;
    } catch { return false; }
}

export function PWAInstallPrompt() {
    const [deferred, setDeferred] = useState(null);
    const [visible, setVisible] = useState(false);
    const [iosHint, setIosHint] = useState(false);

    useEffect(() => {
        if (isStandalone() || isRecentlyDismissed()) return;

        const onBeforeInstall = (e) => {
            e.preventDefault();
            setDeferred(e);
            setVisible(true);
        };
        window.addEventListener("beforeinstallprompt", onBeforeInstall);

        // Safari has no beforeinstallprompt. Show iOS hint after a delay.
        if (isIOS()) {
            const t = setTimeout(() => {
                if (!isStandalone() && !isRecentlyDismissed()) {
                    setIosHint(true);
                    setVisible(true);
                }
            }, 4000);
            return () => {
                clearTimeout(t);
                window.removeEventListener("beforeinstallprompt", onBeforeInstall);
            };
        }
        return () => window.removeEventListener("beforeinstallprompt", onBeforeInstall);
    }, []);

    const dismiss = () => {
        try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch { /* ignore */ }
        setVisible(false);
    };

    const install = async () => {
        if (!deferred) return;
        deferred.prompt();
        try {
            const choice = await deferred.userChoice;
            if (choice?.outcome === "accepted") {
                setVisible(false);
            } else {
                dismiss();
            }
        } catch {
            dismiss();
        } finally {
            setDeferred(null);
        }
    };

    if (!visible) return null;

    return (
        <div
            role="dialog"
            aria-labelledby="pwa-install-title"
            data-testid="pwa-install-prompt"
            className="fixed inset-x-3 bottom-3 sm:left-auto sm:right-4 sm:bottom-4 sm:w-[360px] z-[70] rounded-2xl border border-slate-200 bg-white shadow-xl overflow-hidden"
        >
            <div className="p-4 flex items-start gap-3">
                <div className="w-10 h-10 rounded-xl bg-slate-900 text-amber-400 flex items-center justify-center flex-shrink-0">
                    <Sparkles className="w-5 h-5" />
                </div>
                <div className="flex-1 min-w-0">
                    <p id="pwa-install-title" className="text-sm font-bold text-slate-900">
                        Install Go-Geenie
                    </p>
                    {iosHint ? (
                        <p className="text-[12px] text-slate-600 mt-1 leading-snug">
                            Tap the <span className="inline-flex items-center gap-1 font-semibold">Share</span>{" "}
                            button in Safari, then{" "}
                            <span className="font-semibold">Add to Home Screen</span> to open Geenie like a native app.
                        </p>
                    ) : (
                        <p className="text-[12px] text-slate-600 mt-1 leading-snug">
                            One tap. No app store. Get faster load, offline access & push-worthy AI whispers.
                        </p>
                    )}
                </div>
                <button
                    onClick={dismiss}
                    aria-label="Dismiss install prompt"
                    className="text-slate-400 hover:text-slate-700 flex-shrink-0"
                    data-testid="pwa-install-dismiss"
                >
                    <X className="w-4 h-4" />
                </button>
            </div>
            {!iosHint && (
                <div className="px-4 pb-4">
                    <button
                        onClick={install}
                        className="w-full inline-flex items-center justify-center gap-2 rounded-xl bg-slate-900 text-amber-400 hover:bg-slate-800 font-semibold text-sm py-2.5"
                        data-testid="pwa-install-accept"
                    >
                        <Download className="w-4 h-4" />
                        Install app
                    </button>
                </div>
            )}
        </div>
    );
}
